import {
  ALL_TAB_SECTIONS,
  providerAnswers,
  type SearchProvider,
  type SearchQuery,
  type SearchResult,
  type SearchSection,
} from './search-contract';

export interface RunSearchHandlers {
  /** Aborts every in-flight provider; a new keystroke supersedes the old query. */
  readonly signal: AbortSignal;
  /** One provider finished. Called once per answering provider, in any order. */
  onResults(providerId: string, results: readonly SearchResult[]): void;
  /** One provider failed. Its section shows an error row rather than nothing. */
  onError(providerId: string, error: Error): void;
  /**
   * How long the run waits on one provider before resolving.
   *
   * The provider's promise is not cancelled — late `onResults` / `onError`
   * still fire. Module engines can hang (web `searchGlobal` never returns);
   * without this bound the searching placeholder never yields to empty.
   */
  readonly providerWaitMs?: number;
}

/** Default bound for {@link RunSearchHandlers.providerWaitMs}. */
export const PROVIDER_WAIT_MS = 5_000;

/**
 * Run every provider that answers `query`, delivering each one's rows as they
 * land (架构方案 §5.9).
 *
 * Incremental rather than a single promise for the whole set, and that choice is
 * load-bearing rather than stylistic: the built-in providers read state the
 * renderer already holds and return synchronously, but every future one crosses
 * a process boundary (a module's session index, a third-party app's API). With
 * an all-or-nothing promise the local hits would wait on the slowest remote
 * one, and switching later would mean changing the UI's consumption *and* every
 * provider. So the callback shape is here from the start.
 *
 * Resolves once every answering provider has settled **or** exceeded
 * `providerWaitMs` (default {@link PROVIDER_WAIT_MS}). Callers treat that as
 * "the face may leave searching" — empty can be judged then. A still-running
 * provider may still call `onResults` / `onError` afterwards.
 */
export async function runSearch(
  providers: readonly SearchProvider[],
  query: SearchQuery,
  handlers: RunSearchHandlers,
): Promise<void> {
  const answering = providers.filter((provider) =>
    providerAnswers(provider, query),
  );
  const waitMs = handlers.providerWaitMs ?? PROVIDER_WAIT_MS;

  await Promise.all(
    answering.map((provider) => runProvider(provider, query, handlers, waitMs)),
  );
}

function runProvider(
  provider: SearchProvider,
  query: SearchQuery,
  handlers: RunSearchHandlers,
  waitMs: number,
): Promise<void> {
  return new Promise((resolve) => {
    let released = false;
    const release = (): void => {
      if (released) {
        return;
      }
      released = true;
      resolve();
    };

    const waitTimer = setTimeout(release, waitMs);
    const onAbort = (): void => {
      clearTimeout(waitTimer);
      release();
    };
    if (handlers.signal.aborted) {
      onAbort();
      return;
    }
    handlers.signal.addEventListener('abort', onAbort, { once: true });

    void Promise.resolve()
      .then(() => provider.search(query, handlers.signal))
      .then((results) => {
        clearTimeout(waitTimer);
        if (handlers.signal.aborted) {
          release();
          return;
        }
        handlers.onResults(provider.id, results);
        release();
      })
      .catch((error) => {
        clearTimeout(waitTimer);
        if (handlers.signal.aborted) {
          release();
          return;
        }
        // Never swallowed into "this section just has no hits": the caller shows
        // a visible error row and logs it, so a broken provider looks broken.
        handlers.onError(
          provider.id,
          error instanceof Error ? error : new Error(String(error)),
        );
        release();
      });
  });
}

/** A provider's contribution to the current query, as the host accumulates it. */
export interface ProviderOutcome {
  readonly providerId: string;
  readonly rank: number;
  /**
   * The provider's declared section. Only consulted for {@link error} — a
   * failure has no rows to reveal where it belongs, so the declaration is the
   * only place the error row can go.
   */
  readonly section: SearchSection;
  readonly results?: readonly SearchResult[];
  readonly error?: Error;
}

/** One section's rows, ready to render. */
export interface SearchSectionResults {
  readonly section: SearchSection;
  readonly results: readonly SearchResult[];
  /** Providers that failed while contributing to this section. */
  readonly errors: readonly { providerId: string; error: Error }[];
}

/**
 * Group and order the outcomes collected so far.
 *
 * Pure, and the only ordering authority: sections follow {@link sectionOrder}
 * (platform constants, then module keys the face resolved for this tab), and
 * rows inside a section follow `(provider rank, descending score, title)`.
 * Nothing here truncates — how many rows a section shows is the face's
 * decision, which is what lets a future full-command panel reuse this
 * untouched.
 */
export function mergeResults(
  outcomes: readonly ProviderOutcome[],
  sectionOrder: readonly SearchSection[] = ALL_TAB_SECTIONS,
): SearchSectionResults[] {
  const rankOf = new Map(
    outcomes.map((outcome) => [outcome.providerId, outcome.rank]),
  );
  const bySection = new Map<
    SearchSection,
    { results: SearchResult[]; errors: { providerId: string; error: Error }[] }
  >();

  const bucket = (
    section: SearchSection,
  ): {
    results: SearchResult[];
    errors: { providerId: string; error: Error }[];
  } => {
    let existing = bySection.get(section);
    if (!existing) {
      existing = { results: [], errors: [] };
      bySection.set(section, existing);
    }
    return existing;
  };

  for (const outcome of outcomes) {
    for (const result of outcome.results ?? []) {
      bucket(result.section).results.push(result);
    }
  }
  for (const outcome of outcomes) {
    if (outcome.error) {
      bucket(outcome.section).errors.push({
        providerId: outcome.providerId,
        error: outcome.error,
      });
    }
  }

  const ordered = [...bySection.keys()].sort((a, b) => {
    const indexA = sectionOrder.indexOf(a);
    const indexB = sectionOrder.indexOf(b);
    // Sections the order does not mention keep their discovery order after the
    // declared ones — the face that knows the declarations should pass them.
    const weightA = indexA === -1 ? sectionOrder.length : indexA;
    const weightB = indexB === -1 ? sectionOrder.length : indexB;
    return weightA - weightB;
  });

  return ordered.map((section) => {
    const entry = bucket(section);
    return {
      section,
      results: [...entry.results].sort((a, b) => {
        const rankA = rankOf.get(a.providerId) ?? 0;
        const rankB = rankOf.get(b.providerId) ?? 0;
        if (rankA !== rankB) {
          return rankA - rankB;
        }
        if (a.score !== b.score) {
          return b.score - a.score;
        }
        return a.title.localeCompare(b.title);
      }),
      errors: entry.errors,
    };
  });
}
